import { isNative } from '@/lib/platform'
import { getAccessTokenNoHang } from '@/lib/auth-token'
import { appVersionTag } from '@/lib/app-version'

/**
 * Base origin to prefix onto `/api` calls.
 *
 * - **Web:** `''` — calls stay relative and resolve same-origin (nginx serves
 *   the SPA and proxies `/api` on one host; dev uses the Vite proxy).
 * - **Native (bundled):** the app origin, so `/api` reaches the real backend.
 *   The webview origin is `capacitor://localhost` (iOS) / `https://localhost`
 *   (Android), where no server lives.
 * - **Native (livereload):** dev-on-device (`cap run --livereload`) serves the
 *   app over `http(s)` from a LAN host whose Vite proxy forwards `/api` — so we
 *   keep relative URLs and return `''` there too.
 *
 * The base is `VITE_API_URL` if set (escape hatch for a future split API host),
 * otherwise `VITE_APP_URL` (the API is same-origin with the app today).
 */
export function apiBase(): string {
  if (!isNative()) return ''
  const { protocol, hostname } = window.location
  // Livereload: served from a real http(s) dev host → relative + proxy works.
  // Bundled native assets load from `capacitor://` (iOS) or `https://localhost`
  // (Android), so only treat `localhost` over http(s) as "bundled".
  if ((protocol === 'http:' || protocol === 'https:') && hostname !== 'localhost') return ''
  // Dev live-reload over `adb reverse` is served from `http://localhost:5173`
  // (http + localhost = secure context for getUserMedia). Bundled Android is
  // `https://localhost` and iOS is `capacitor://localhost`, never plain http —
  // so `http:` here means the dev server: keep relative URLs so the Vite proxy
  // forwards /api to the local Hono backend same-origin (no CORS).
  if (protocol === 'http:') return ''
  return process.env.VITE_API_URL || process.env.VITE_APP_URL || ''
}

/**
 * `fetch` for backend (`/api/...`) calls. Prefixes {@link apiBase} and, on
 * native only, attaches the Supabase access token as a bearer header — native
 * is cross-origin to the backend so the auth cookie isn't sent. Web is byte-
 * identical to a bare `fetch` (relative URL, cookie auth via `credentials`).
 *
 * Returns the raw `Response` (no body parsing) so blob/PDF/export callers keep
 * working. Non-`/api` paths and absolute URLs pass through untouched.
 */
export async function apiFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const isApiPath = path.startsWith('/api')
  const url = isApiPath ? `${apiBase()}${path}` : path

  // Native + backend call: attach the bearer token. We only touch headers on
  // this path so web stays byte-identical to a bare `fetch` (callers' header
  // objects pass through unchanged).
  if (isNative() && isApiPath) {
    const headers = new Headers(init.headers)
    // Stamp the native app build so the server can record analyses.app_version
    // (diagnostics: which store version a user is on). Native only — the web
    // path below stays byte-identical to a bare fetch by design; the web build
    // is already knowable from /api/version.
    const version = appVersionTag()
    if (version && !headers.has('X-App-Version')) headers.set('X-App-Version', version)
    if (!headers.has('Authorization')) {
      // getAccessTokenNoHang reads the persisted token (no network) so a stale
      // token offline can't hang every native /api call — including the
      // ConnectivityService /api/version probe, whose AbortSignal only bounds
      // the fetch, not the token fetch before it. See auth-token.ts.
      const token = await getAccessTokenNoHang()
      if (token) headers.set('Authorization', `Bearer ${token}`)
    }
    // 'omit', NOT 'include'. Native authenticates with the bearer token above,
    // never cookies — it's cross-origin to the backend. 'include' makes this a
    // *credentialed* CORS request, which the browser blocks unless the server
    // returns Access-Control-Allow-Credentials: true. The server intentionally
    // doesn't (see server/index.ts cors() — bearer, not cookies), so 'include'
    // silently fails every native /api call: the ConnectivityService
    // HEAD /api/version probe throws and the app shows "offline" despite sync
    // working. 'omit' keeps it a plain CORS request that only needs the origin
    // allow-listed, which the server does provide.
    //
    // `credentials` MUST come AFTER `...init`: most callers hardcode
    // credentials:'include' (the web cookie-auth default), and if `...init`
    // spread last it would clobber 'omit' back to 'include' and re-break every
    // native call (burst /api/ai/analyze, single-image analyze, defect types …).
    return fetch(url, { ...init, credentials: 'omit', headers })
  }

  return fetch(url, { credentials: 'include', ...init })
}
